import * as _ from 'lodash';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { DropdownTreeItem } from 'fc-ui-components';
import { LocationService } from '@fashioncloud/location-service-ngx';
import {
    CreateLocationRequest,
    Location,
    SourceMethod,
    Store,
    UpdateLocationRequest
} from '@fashioncloud/location-service-types';
import { UserService } from 'app/shared/services';
export type LocationStore = Location & Partial<Store>;

@Injectable({ providedIn: 'root' })
export class LocationStoreService {
    public stores: BehaviorSubject<LocationStore[]> = new BehaviorSubject([]);
    public storesIncludingDisabled: BehaviorSubject<LocationStore[]> = new BehaviorSubject([]);
    public storeDropdown: Observable<DropdownTreeItem<LocationStore>[]>;
    public hasError: BehaviorSubject<boolean>;
    public locationService = inject(LocationService);
    public userService = inject(UserService);

    constructor() {
        this.storeDropdown = this.stores.pipe(
            map((stores) =>
                stores.map((store) => {
                    const { address, name, shortName } = store;
                    const { address1, zip, city } = address;

                    let label = '';

                    if (shortName) {
                        label = shortName;
                    } else if (name) {
                        label = name;
                    } else {
                        label = `${address1}, ${zip} ${city}`;
                    }

                    return { value: store, label };
                })
            )
        );

        this.hasError = new BehaviorSubject<boolean>(false);
    }

    public resetHasError(): void {
        this.hasError.next(false);
    }

    // only used in the edit store page
    public updateStore(id: string, store: UpdateLocationRequest) {
        this.hasError.next(false);
        return this.locationService.updateLocation(id, { ...store, sourceMethod: SourceMethod.UserInput }).pipe(
            tap(() => this.pullStores()),
            catchError((error) => {
                console.error(`Error updating store with GLN ${store.gln}`, error);
                this.hasError.next(true);
                throw error;
            })
        );
    }

    // affects b2b create store from dialog
    public createStore(store: CreateLocationRequest) {
        this.hasError.next(false);
        return this.locationService.createLocation(store).pipe(
            tap(() => this.pullStores()),
            catchError((error) => {
                console.error(`Error creating store with GLN ${store.gln}`, error);
                this.hasError.next(true);
                throw error;
            })
        );
    }

    public deleteStore(id: string) {
        this.hasError.next(false);
        return this.locationService.deleteLocation(id).pipe(
            catchError((error) => {
                console.error(`Error deleting store with GLN ${id}`, error);
                this.hasError.next(true);
                throw error;
            }),
            tap(() => this.pullStores())
        );
    }

    // used also on preorder
    public pullStores() {
        const organizationId = this.userService.getOrganizationId();
        this.locationService
            .getLocations({ organizationId })
            .pipe(
                catchError((error) => {
                    console.error(`Stores could not be loaded`, error);
                    return of({ locations: [] as LocationStore[] });
                })
            )
            .subscribe(({ locations }) => {
                if (_.isEmpty(locations)) {
                    this.storesIncludingDisabled.next([]);
                    this.stores.next([]);
                    return;
                }
                this.storesIncludingDisabled.next(locations);
                this.stores.next(locations.filter(({ isEnabled }) => isEnabled));
            });
    }

    // single store and stores modal
    public fetchStoresObservable() {
        return this.locationService
            .getLocations()
            .pipe(map(({ locations }) => locations.filter((location) => location.isEnabled)));
    }

    public getLocationByGln(gln: string) {
        return this.locationService.getLocations({ gln });
    }

    public getLocationById(id: string) {
        return this.locationService.getLocationById(id);
    }
}
